// ============================================================
// AGRIDA — Limit Proposal Engine
// Công thức đề xuất hạn mức dựa trên giá trị hợp đồng và risk profile
// ============================================================

import type { RiskLevel } from '@/types';

/**
 * Tỷ lệ đề xuất hạn mức theo mức độ rủi ro.
 * - Tốt: 60% giá trị hợp đồng
 * - Khá: 50%
 * - Cần chú ý: 35%
 * - Rủi ro cao: 20%
 * - Nguy cơ: 10%
 * - Chưa tính: 15% (mặc định thận trọng)
 */
const RISK_PROPOSAL_RATIO: Record<string, number> = {
  'Tốt': 0.60,
  'Khá': 0.50,
  'Cần chú ý': 0.35,
  'Rủi ro cao': 0.20,
  'Nguy cơ': 0.10,
  'Không đạt': 0,
  'Chưa tính': 0.15,
};

export interface LimitProposalResult {
  /** Hạn mức đề xuất cơ bản = giá trị HĐ × tỷ lệ risk */
  suggestedLimit: number;
  /** Tỷ lệ đề xuất đã áp dụng */
  ratio: number;
  /** Mức rủi ro đầu vào */
  riskLevel: string;
  /** Tỷ lệ phần trăm hiển thị */
  ratioPercent: string;
  /** Mô tả ngắn gọn về đề xuất */
  description: string;
  /** Hạn mức tối đa có thể cấp (không vượt giá trị HĐ) */
  maxLimit: number;
  /** Hạn mức còn có thể cấp từ hợp đồng */
  remainFromContract: number;
}

/**
 * Tính hạn mức đề xuất cho một hợp đồng.
 * @param contractValue - Giá trị hợp đồng
 * @param riskLevel - Mức rủi ro của nông hộ
 * @param hanMucDaCap - Hạn mức đã cấp từ hợp đồng này
 * @param creditScore - Điểm tín dụng (optional, để tinh chỉnh)
 */
export function calculateLimitProposal(
  contractValue: number,
  riskLevel: RiskLevel | string | undefined,
  hanMucDaCap: number = 0,
  creditScore?: number,
): LimitProposalResult {
  const ratio = RISK_PROPOSAL_RATIO[riskLevel || 'Chưa tính'] || 0.15;

  // Điều chỉnh nhẹ dựa trên credit score nếu có
  let adjustedRatio = ratio;
  if (creditScore !== undefined && creditScore > 0) {
    if (creditScore >= 760) adjustedRatio = Math.min(0.70, ratio + 0.05);
    else if (creditScore >= 650) adjustedRatio = Math.min(0.60, ratio + 0.03);
    else if (creditScore < 450) adjustedRatio = Math.max(0.05, ratio - 0.05);
  }

  const rawSuggested = Math.round(contractValue * adjustedRatio);
  const maxLimit = contractValue;
  const remainFromContract = Math.max(0, maxLimit - hanMucDaCap);
  const suggestedLimit = Math.min(rawSuggested, remainFromContract);

  const descriptions: Record<string, string> = {
    'Tốt': 'Nông hộ có lịch sử tín dụng tốt, đề xuất mức cao',
    'Khá': 'Nông hộ đáng tin cậy, đề xuất mức trung bình cao',
    'Cần chú ý': 'Cần giám sát, đề xuất mức trung bình',
    'Rủi ro cao': 'Rủi ro cao, đề xuất mức thấp - cần HTX xác nhận',
    'Nguy cơ': 'Rủi ro rất cao, đề xuất mức tối thiểu - cần xác nhận đặc biệt',
    'Không đạt': 'Không đủ điều kiện cấp hạn mức',
    'Chưa tính': 'Chưa có đánh giá rủi ro - đề xuất mức thận trọng',
  };

  return {
    suggestedLimit,
    ratio: adjustedRatio,
    riskLevel: riskLevel || 'Chưa tính',
    ratioPercent: `${Math.round(adjustedRatio * 100)}%`,
    description: descriptions[riskLevel || 'Chưa tính'] || 'Đề xuất mức thận trọng',
    maxLimit,
    remainFromContract,
  };
}

/**
 * Kiểm tra hạn mức đề xuất có vượt giá trị hợp đồng không.
 */
export function isOverContract(proposedAmount: number, contractValue: number): boolean {
  return proposedAmount > contractValue;
}

/**
 * Kiểm tra hạn mức đề xuất có vượt hạn mức HTX còn lại không.
 */
export function isOverHtxRemaining(proposedAmount: number, htxRemaining: number): boolean {
  return proposedAmount > htxRemaining;
}

/**
 * Lấy danh sách mức rủi ro kèm tỷ lệ để hiển thị trong UI.
 */
export function getRiskRatioTable(): { risk: string; ratio: string; color: string }[] {
  return [
    { risk: 'Tốt', ratio: '60%', color: 'text-emerald-600' },
    { risk: 'Khá', ratio: '50%', color: 'text-teal-600' },
    { risk: 'Cần chú ý', ratio: '35%', color: 'text-amber-600' },
    { risk: 'Rủi ro cao', ratio: '20%', color: 'text-orange-600' },
    { risk: 'Nguy cơ', ratio: '10%', color: 'text-red-600' },
    { risk: 'Chưa tính', ratio: '15%', color: 'text-slate-500' },
  ];
}